import { FC } from 'react'

import { useQueryApi } from 'peach/data/useQueryApi'

import { R } from 'core/helpers'
import { isNotFoundError } from 'core/helpers/error'
import { useGetCaseWorkflow, useGetWorkflowTypeById } from 'core/types'

import JsonDisplayValue from '../JsonDisplayValue'
import ModelLink from './ModelLink'

type WorkflowSmartLinkProps = {
  workflowId: string
  caseId?: string
}

const WorkflowSmartLink: FC<WorkflowSmartLinkProps> = ({
  workflowId,
  caseId: passedCaseId,
}) => {
  const [_, { companyId, personId, caseId: contextCaseId }] = useQueryApi()

  const caseId = passedCaseId || contextCaseId

  // Firstly try to get as Workflow Type
  const {
    data: workflowType,
    isLoading: isLoadingType,
    isError: isErrorType,
    error: errorType,
  } = useGetWorkflowTypeById({
    companyId,
    workflowTypeId: workflowId,
    options: {
      throwOnError: R.isNot(isNotFoundError),
    },
  })

  // If this is not a Workflow Type (404) and there is a caseId - try to get as Workflow instance
  const isNotFoundType = isErrorType && isNotFoundError(errorType)
  const { data: workflow, isLoading: isLoadingInstance } = useGetCaseWorkflow({
    companyId,
    personId,
    caseId,
    options: {
      enabled: isNotFoundType && !!(companyId && personId && caseId),
      throwOnError: R.isNot(isNotFoundError),
    },
  })

  const isLoading = isLoadingType || isLoadingInstance

  // If found Workflow Type - link to Workflow Type page
  if (workflowType) {
    const link =
      companyId && `/companies/${companyId}/workflow-types/${workflowId}`
    return (
      <ModelLink
        id={workflowId}
        isLoading={isLoading}
        link={link}
        model={workflowType}
      />
    )
  }

  // If found Workflow instance - link to Case page with workflow tab
  if (workflow && caseId) {
    const link =
      companyId &&
      personId &&
      caseId &&
      `/companies/${companyId}/borrowers/${personId}/cases/${caseId}/workflow`
    return (
      <ModelLink
        id={workflowId}
        isLoading={isLoading}
        link={link}
        model={workflow}
      />
    )
  }

  // If caseId is present, but workflow is still loading - show link to Case/workflow
  if (caseId && companyId && personId) {
    const link = `/companies/${companyId}/borrowers/${personId}/cases/${caseId}/workflow`
    return (
      <ModelLink
        id={workflowId}
        isLoading={isLoading}
        link={link}
        model={undefined}
      />
    )
  }

  // If nothing is found and there is no caseId - show as a regular ID
  return <JsonDisplayValue type='id'>{workflowId}</JsonDisplayValue>
}

export default WorkflowSmartLink
